import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, filter, lastValueFrom, map, Observable, switchMap } from 'rxjs';
import { ReCaptchaConfig, ReCaptchaConfigArgs } from '../type/recaptcha.type';

@Injectable({ providedIn: 'root' })
export class ReCaptchaConfigService {
  private readonly configLoaded$ = new BehaviorSubject<boolean>(false);
  private readonly http = inject(HttpClient);
  private config: ReCaptchaConfig | null = null;

  async loadConfig(args: ReCaptchaConfigArgs): Promise<void> {
    if (args.siteKey) {
      this.setConfigAndNotifyLoaded(args.siteKey);
      return;
    }

    if (!args.config) return;

    return lastValueFrom(this.http.get<Record<string, unknown>>(args.config!.url))
      .then(config => {
        const siteKey = config[args.config!.key];
        if (typeof siteKey !== 'string') throw new Error('Invalid ReCaptcha Site key');

        this.setConfigAndNotifyLoaded(siteKey);
      })
      .catch(error => {
        this.configLoaded$.error(error);
        throw error;
      });
  }

  setSiteKey(siteKey: string): void {
    this.setConfigAndNotifyLoaded(siteKey);
  }

  waitForConfig(): Observable<ReCaptchaConfig> {
    return this.configLoaded$.asObservable().pipe(
      filter(loaded => loaded === true),
      map(() => this.getApiConfig())
    );
  }

  withConfig<T>(requestFn: (config: ReCaptchaConfig) => Observable<T>): Observable<T> {
    return this.waitForConfig().pipe(switchMap(c => requestFn(c)));
  }

  getApiConfig(): ReCaptchaConfig {
    if (!this.config) {
      throw new Error('ReCaptcha config not initialized. Ensure config is loaded first.');
    }

    return this.config;
  }

  private setConfigAndNotifyLoaded(siteKey: string): void {
    this.config = { siteKey };
    this.configLoaded$.next(true);
  }
}
